import { useEffect, useState } from 'react';

import { Box, Stack, TextField } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { EditDrawer } from 'common/components/EditDrawer';
import {
    closeDrawer,
    selectCurrentDrawerId,
    selectNameInConflict,
    selectOpen,
    setNameInConflict,
    setSaveButtonEnabled
} from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { PageInfo, selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { getOptionLabelDefaultCallback,isOptionEqualToValueDefaultCallback } from 'common/components/sharedCustomComponents/Common/helpers';
import { EntitiesSearchableCommonDropDown } from 'common/components/sharedCustomComponents/EntitiesSearchableCommonDropDown/EntitiesSearchableCommonDropDown';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { postUserGroupRoleMembership } from 'common/services/api/UserGroupRoleMembership/UserGroupRoleMembership.thunk';
import { fetchUserGroups } from 'common/services/api/UserGroups/UserGroups.thunk';
import { UserGroup } from 'common/types/Entities';
import { EditDrawerContentProps, Operations } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { handleEditDrawerError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

const EXISTING_USER_GROUP_LINKING_ERROR_MESSAGE = 'This User Group is already linked to this Role.';
const USER_GROUP_ROLE_MEMBERSHIP_POST_SUCCES_MESSAGE = 'The User Group was successfully linked.';

const constructCreateContentComponent = (
    title: string,
    pageInfo: PageInfo | undefined,
    saveButtonFunction: () => void,
    processing: boolean,
    userGroup: UserGroup | null,
    handleUserGroupChange: (e: unknown, userGroup: UserGroup | null) => void,
    nameInConflict: boolean,
) => (
    <EditDrawer
        title={title}
        saveButtonFunction={saveButtonFunction}
        processing={processing}
    >
        <Stack spacing={2}>
            <EntitiesSearchableCommonDropDown
                initialEntityOption={{ id: '', name: '',description: '', tenantId: '' }}
                value={userGroup}
                onChange={handleUserGroupChange}
                label='User Group Name'
                getOptionLabel={getOptionLabelDefaultCallback}
                isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                asyncThunkAction={fetchUserGroups}
                asyncThunkArgs={{ tenantId: pageInfo?.tenantId ?? '' }}
                displayMenuItemContent={(option: UserGroup) => <>{option.name}</>}
                hasError={nameInConflict}
                effectDependecyArray={[pageInfo?.tenantId ?? '']}
            />
            {nameInConflict &&
                <Box component='div'
                    display='inline'
                    sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                >
                    {EXISTING_USER_GROUP_LINKING_ERROR_MESSAGE}
                </Box>
            }
            <TextField
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '4px',
                }}
                size="small"
                label="User Group Description"
                variant="filled"
                value={userGroup?.description ?? ''}
                disabled
            />
            <TextField
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '4px',
                }}
                size="small"
                label="Role Name"
                variant="filled"
                defaultValue={pageInfo?.name ?? ''}
                disabled
            />
            <TextField
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '4px',
                }}
                size="small"
                label="Tenant Name"
                variant="filled"
                defaultValue={pageInfo?.tenantName ?? ''}
                disabled
            />
        </Stack>
    </EditDrawer>
);

export function UserGroupRoleMembershipCreateContent({
    title,
}: EditDrawerContentProps<{ name: string }>) {
    const [userGroup, setUserGroup] = useState<UserGroup | null>(null);
    const pageInfo = useAppSelector(selectPageInfo);
    const drawerOpen = useAppSelector(selectOpen);
    const currentId = useAppSelector(selectCurrentDrawerId);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if(currentId === DrawerIDs.UserGroup){
            setUserGroup(null);
        }
    }, [drawerOpen]);

    const [processing, setProcessing] = useState<boolean>(false);
    const nameInConflict = useAppSelector(selectNameInConflict);
    const handleUserGroupChange = (_e: unknown, newUserGroup: UserGroup | null) => {
        setUserGroup(newUserGroup);
        dispatch(setSaveButtonEnabled(!!newUserGroup?.id));
        dispatch(setNameInConflict(false));
    };

    const dispatch = useAppDispatch();
    const userGroupPostCallback = () => {
        setProcessing(true);
        const userGroupOptions = {
            tenantId: pageInfo?.tenantId ?? '',
            roleId: pageInfo?.id ?? '',
            userGroupId: userGroup?.id ?? ''
        };

        dispatch(postUserGroupRoleMembership(userGroupOptions))
            .unwrap()
            .then((result) => {
                enqueueSnackbar(USER_GROUP_ROLE_MEMBERSHIP_POST_SUCCES_MESSAGE, { variant: 'success' });
                dispatch(setRows(result.rows));
                dispatch(setNameInConflict(false));
                dispatch(closeDrawer());
            })
            .catch((error) => {
                handleEditDrawerError(Operations.Link, extractStatusCode(error.message), 'User Group', dispatch, enqueueSnackbar);
            })
            .finally(() => {
                setProcessing(false);
            });
    };

    return constructCreateContentComponent(
        title,
        pageInfo,
        userGroupPostCallback,
        processing,
        userGroup,
        handleUserGroupChange,
        nameInConflict ?? false,
    );
}
